import { useQuery } from "@tanstack/react-query";
import { bridgeApiService } from "@/services/bridge-api.service";
import { BRIDGE_NETWORKS, BRIDGE_ASSETS } from "@/config";
import {
  EstimatedExchangeRequest,
  BridgeEstimationParams,
} from "@/types/bridge.types";

export const useBridgeEstimation = ({
  fromNetwork,
  toNetwork,
  amount,
  enabled = true,
}: BridgeEstimationParams) => {
  return useQuery({
    queryKey: ["bridge-estimation", fromNetwork, toNetwork, amount],
    queryFn: async () => {
      if (!amount || parseFloat(amount) <= 0) {
        throw new Error("Please enter a valid amount greater than 0");
      }

      const request: EstimatedExchangeRequest = {
        assetFrom: BRIDGE_ASSETS.A1X,
        networkFrom: fromNetwork,
        assetTo: BRIDGE_ASSETS.A1X,
        networkTo: toNetwork,
        amountFrom: amount,
      };

      try {
        return await bridgeApiService.getEstimatedExchange(request);
      } catch (error) {
        // Enhanced error handling for better user experience
        if (error instanceof Error) {
          if (error.message.includes("timeout")) {
            throw new Error(
              "Request timed out. Please check your connection and try again."
            );
          }
          if (error.message.includes("Network Error")) {
            throw new Error(
              "Network connection failed. Please check your internet and try again."
            );
          }
          if (
            error.message.includes("500") ||
            error.message.includes("Internal Server")
          ) {
            throw new Error(
              "Service temporarily unavailable. Please try again in a moment."
            );
          }
          if (
            error.message.includes("400") ||
            error.message.includes("Bad Request")
          ) {
            throw new Error(
              "Invalid request parameters. Please check your input and try again."
            );
          }
        }
        throw error;
      }
    },
    enabled: enabled && !!amount && parseFloat(amount) > 0,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: false, // Disable automatic refetching to prevent duplicates
    retry: (failureCount, error) => {
      // Don't retry validation errors
      if (error instanceof Error && error.message.includes("valid amount")) {
        return false;
      }
      // Retry network errors up to 2 times
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
  });
};
